// User roles in the system
export type UserRole = 'admin' | 'manager' | 'viewer' | 'employee';

// 🔧 FIX C12: 허용 역할 런타임 검증용 상수
export const ALLOWED_ROLES: readonly UserRole[] = ['admin', 'manager', 'viewer', 'employee'] as const;

export function isValidRole(role: string): role is UserRole {
  return ALLOWED_ROLES.includes(role as UserRole);
}

// Permission types for different actions
export type Permission =
  | 'view_teams'
  | 'edit_teams'
  | 'view_performance'
  | 'edit_performance'
  | 'view_payroll'
  | 'edit_payroll'
  | 'edit_payroll_limited'   // 급여 제한 편집 (연장근로시간·개별수당만) — 실장용
  | 'view_sales'
  | 'edit_sales'
  | 'view_daily_report'
  | 'edit_daily_report'
  | 'view_settings'
  | 'edit_settings'
  | 'manage_users'
  | 'view_annual_leave'      // 연차 조회
  | 'edit_annual_leave'      // 연차 신청
  | 'approve_annual_leave'   // 연차 승인 (관리자)
  | 'manage_leave_settings'; // 연차 설정 관리

/**
 * 🔒 P2 보안 개선: Feature 타입 정의
 * useCanEdit, useCanView 훅에서 사용하는 feature 문자열의 타입 안정성 확보
 *
 * 예시:
 * - useCanEdit('teams') → 'edit_teams' 권한 검증
 * - useCanView('payroll') → 'view_payroll' 권한 검증
 */
export type FeatureName =
  | 'teams'
  | 'performance'
  | 'payroll'
  | 'sales'
  | 'daily_report'
  | 'settings'
  | 'annual_leave';

// 타입 가드: view_* 권한 생성
export type ViewPermission = `view_${FeatureName}`;

// 타입 가드: edit_* 권한 생성
export type EditPermission = `edit_${FeatureName}`;

// Feature별 권한 매핑 (컴파일 타임 검증용)
export const FEATURE_PERMISSIONS: Record<FeatureName, { view: Permission; edit: Permission }> = {
  teams: { view: 'view_teams', edit: 'edit_teams' },
  performance: { view: 'view_performance', edit: 'edit_performance' },
  payroll: { view: 'view_payroll', edit: 'edit_payroll' },
  sales: { view: 'view_sales', edit: 'edit_sales' },
  daily_report: { view: 'view_daily_report', edit: 'edit_daily_report' },
  settings: { view: 'view_settings', edit: 'edit_settings' },
  annual_leave: { view: 'view_annual_leave', edit: 'edit_annual_leave' }
};

// 모든 권한 목록을 배열로도 제공
export const ALL_PERMISSIONS: Permission[] = [
  'view_teams',
  'edit_teams',
  'view_performance',
  'edit_performance',
  'view_payroll',
  'edit_payroll',
  'edit_payroll_limited',
  'view_sales',
  'edit_sales',
  'view_daily_report',
  'edit_daily_report',
  'view_settings',
  'edit_settings',
  'manage_users',
  'view_annual_leave',
  'edit_annual_leave',
  'approve_annual_leave',
  'manage_leave_settings'
];

// 권한별 한국어 설명
export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  view_teams: '팀 목록 및 팀 구성원 조회',
  edit_teams: '팀 생성, 수정, 삭제 및 구성원 변경',
  view_performance: '팀 성과 및 인센티브 현황 조회',
  edit_performance: '인센티브 비율 및 성과 데이터 수정',
  view_payroll: '급여 내역 및 월별 통계 조회',
  edit_payroll: '급여 데이터 수정 및 예산 관리',
  edit_payroll_limited: '급여 제한 편집 — 연장근로시간·개별수당만 입력·저장 (실장용)',
  view_sales: '어시스트별 월 수납 현황 조회',
  edit_sales: '수납 데이터 수정',
  view_daily_report: '데일리 리포트 조회',
  edit_daily_report: '데일리 리포트 엑셀 업로드 및 수정',
  view_settings: '시스템 설정 조회',
  edit_settings: '시스템 설정 변경',
  manage_users: '사용자 계정 및 역할 관리',
  view_annual_leave: '연차 현황 조회',
  edit_annual_leave: '연차 신청',
  approve_annual_leave: '연차 승인 및 반려',
  manage_leave_settings: '연차 설정 관리 (기본 일수, 정책 등)'
};

// User interface
export interface User {
  uid: string;
  email: string;
  displayName: string | null;
  phoneNumber?: string;  // 전화번호 필드 추가
  role: UserRole;
  permissions: Permission[];        // 역할 기본 권한
  customPermissions?: Permission[]; // 개별 추가 권한
  revokedPermissions?: Permission[]; // 제거된 권한
  teamId?: string;
  // 🔗 직원 명단(employees)의 id — 본인 급여 열람 판정에 사용한다.
  //    이름 자동 매칭은 동명이인 오매칭 위험이 있어 금지하며, 관리자가 화면에서 수동 지정한다.
  employeeId?: string;
  createdAt: Date;
  lastLogin: Date;
}

// Authentication state
export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

// Login credentials
export interface LoginCredentials {
  email: string;
  password: string;
}

// Registration data
export interface RegistrationData extends LoginCredentials {
  displayName: string;
  role?: UserRole;
}

// Role configuration with permissions
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: [
    'view_teams',
    'edit_teams',
    'view_performance',
    'edit_performance',
    'view_payroll',
    'edit_payroll',
    'edit_payroll_limited',
    'view_sales',
    'edit_sales',
    'view_daily_report',
    'edit_daily_report',
    'view_settings',
    'edit_settings',
    'manage_users',
    'view_annual_leave',
    'edit_annual_leave',
    'approve_annual_leave',
    'manage_leave_settings'
  ],
  manager: [
    'view_teams',
    'edit_teams',
    'view_performance',
    'edit_performance',
    'view_payroll',
    // 🔒 급여 전체 편집(edit_payroll)은 주지 않는다 — 연장근로시간·개별수당만 편집 가능
    'edit_payroll_limited',
    'view_sales',
    'edit_sales',
    'view_daily_report',
    'edit_daily_report',
    'view_settings',
    'view_annual_leave',
    'edit_annual_leave',
    'approve_annual_leave'
  ],
  viewer: [
    'view_teams',
    'view_performance',
    'view_payroll',
    'view_sales',
    'view_daily_report',
    'view_settings',
    'view_annual_leave'
  ],
  employee: [
    'view_performance',
    'view_daily_report',
    'edit_daily_report',
    'view_annual_leave',
    'edit_annual_leave'
  ]
};

// Route access configuration
export const ROUTE_PERMISSIONS: Record<string, Permission[]> = {
  '/': ['view_performance'],
  '/teams': ['view_teams'],
  '/performance': ['view_performance'],
  '/payroll': ['view_payroll'],
  '/sales': ['view_sales'],
  '/daily-report': ['view_daily_report'],
  '/settings': ['view_settings'],
  '/annual-leave': ['view_annual_leave'],
  '/admin/annual-leave': ['approve_annual_leave', 'manage_leave_settings']
};